import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../../services/apiService';

/**
 * Contexto de autenticación con datos del usuario, rol y permisos
 *
 * Provee: userName, userRol, isAdmin(), hasPermission(permiso)
 * Se actualiza en el evento 'authChange' (disparado al login/logout).
 */
const AuthContext = createContext(null);

/**
 * Proveedor del contexto de autenticación
 *
 * @param {Object} props - Propiedades del proveedor
 * @param {React.ReactNode} props.children - Árbol de componentes que tendrán acceso al contexto de autenticación
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  const getAuthData = useCallback(() => ({
    userName: localStorage.getItem('userName') || 'Usuario',
    userRol: apiService.getUserRol()
  }), []);

  const [authData, setAuthData] = useState(getAuthData);
  const [permisos, setPermisos] = useState(new Set());
  const [permisosLoaded, setPermisosLoaded] = useState(false);

  const loadPermisos = useCallback(async () => {
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuth) {
      setPermisos(prev => prev.size === 0 ? prev : new Set());
      setPermisosLoaded(true);
      return;
    }
    try {
      const data = await apiService.getMisPermisos();
      if (data.success) {
        setPermisos(prev => {
          const newSet = new Set(Array.isArray(data.data) ? data.data : []);
          if (newSet.size === prev.size && [...newSet].every(p => prev.has(p))) return prev;
          return newSet;
        });
      }
    } catch {
      setPermisos(prev => prev.size === 0 ? prev : new Set());
    } finally {
      setPermisosLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadPermisos();
  }, [loadPermisos]);

  useEffect(() => {
    const handleAuthChange = () => {
      setAuthData(getAuthData());
      loadPermisos();
    };
    window.addEventListener('authChange', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('authChange', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [getAuthData, loadPermisos]);

  const isAdmin = useCallback(() => authData.userRol === 'ADMIN', [authData.userRol]);
  const hasPermission = useCallback((permiso) => permisos.has(permiso), [permisos]);

  const value = useMemo(() => ({
    userName: authData.userName,
    userRol: authData.userRol,
    isAdmin,
    hasPermission,
    permisosLoaded
  }), [authData.userName, authData.userRol, isAdmin, hasPermission, permisosLoaded]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook para acceder al contexto de autenticación
 *
 * @returns {{ userName: string, userRol: string|null, isAdmin: function, hasPermission: function }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}

export default AuthContext;
